import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';
import { combinedDeviceDossierUrl, type DossierResponse } from '@/features/dossier/api';
import { useCombinedDeviceDossier } from '@/features/dossier/hooks';
import { NiIvdrDossierHeader } from './DossierResultRenderer';
import { SectionBlock, type RenderedSection } from './CombinedDossierSection';
import { GsprMatrixRenderer } from './GsprMatrixRenderer';
import { ivdrDossierProfile } from './euIvdrDossierProfile';
import {
  PartPendingNote,
  pickDossier,
  pickSubmissionHeader,
  type DossierRenderCtx,
} from './euDossierProfile';

/**
 * The COMPLETE NI IVDR dossier rendered on the result page — mirrors
 * `CombinedEuDossierView` exactly but fetches from the NI backend endpoint
 * (`/api/products/{id}/ni-ivdr-dossier`) so it assembles from NI runs
 * (TD / PMS / SSP) rather than the EU ones.
 *
 * NI is always IVDR (CE-UKNI mark, EU-NI AR required), so this component
 * drops the MDR branch and always uses `ivdrDossierProfile`. Header framing
 * (CE-UKNI label, "Northern Ireland" copy, EU-NI AR fields) is a LATER pass;
 * the priority here is routing the fetch to the correct endpoint so the
 * agent parts render.
 */

export function CombinedNiIvdrDossierView({
  productId,
  deviceClass,
  runId = null,
  productType = null,
}: {
  productId: number;
  deviceClass: string;
  /** Any run of the dispatch this complete dossier belongs to: pins the page and its Download
   *  PDF to that dispatch (spec 2026-10-23 B). Null = the product's latest completed dispatch. */
  runId?: number | null;
  /** Product type for the provenance header (annexes don't carry it; threaded
   *  from the representative run's dossier_response). */
  productType?: string | null;
}) {
  const profile = ivdrDossierProfile;
  const url = (fmt: 'pdf') => combinedDeviceDossierUrl('ni-ivdr', productId, deviceClass, fmt, runId);

  const { parts, failed } = useCombinedDeviceDossier('ni-ivdr', productId, deviceClass, runId);

  if (failed) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <Button variant="brand" size="sm" asChild>
            <a
              href={url('pdf')}
              target="_blank"
              rel="noopener"
              title="Download the full NI IVDR dossier as PDF"
            >
              <Download className="size-3.5" /> Download PDF
            </a>
          </Button>
        </div>
        <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6 text-state-error-text text-sm">
          Couldn't load the complete dossier in the page. Use Download PDF, or refresh.
        </div>
      </div>
    );
  }

  if (parts === null) {
    return <RunLoadingSkeleton label="Loading the complete dossier" />;
  }

  // Build a minimal DossierResponse for the shared EuDossierHeader.
  const annexHeader =
    pickSubmissionHeader(parts.td) ??
    pickSubmissionHeader(parts.pms) ??
    pickSubmissionHeader(parts.sscp);
  const annexDossier =
    pickDossier(parts.td) ?? pickDossier(parts.pms) ?? pickDossier(parts.sscp) ?? {};
  const headerResponse = {
    product_id: String(productId),
    product_name: parts.device_name ?? '',
    product_type: productType ?? '',
    submission_type: parts.device_class,
    model: '',
    iterations: 0,
    dossier: annexDossier,
    submission_header: annexHeader ?? undefined,
    tool_calls: [],
    warnings: [],
    dossier_run_id: 0,
  } as DossierResponse;

  const ctx: DossierRenderCtx = { productId, deviceClass };

  // ── NUMBERED PARTS ─────────────────────────────────────────────────────────
  const numbered: RenderedSection[] = profile.numberedParts
    .filter((spec) => spec.applicable(deviceClass))
    .map((spec, i) => {
      const part = spec.pick(parts);
      return {
        id: spec.id,
        title: `Part ${i + 1} — ${spec.title}`,
        body: part == null ? <PartPendingNote /> : spec.render(part, ctx),
      };
    });

  // ── GSPR — shared appendix interleaved right after the TD Part ──────────────
  const gspr: RenderedSection | null = parts.gspr_matrix
    ? {
        id: 'eu-sec-gspr_matrix',
        title: 'GSPR Conformity Checklist (Annex I)',
        body: <GsprMatrixRenderer matrix={parts.gspr_matrix as Record<string, unknown>} />,
      }
    : null;

  // ── APPENDICES — after all numbered Parts (present-only, not numbered) ───────
  const appendixSections: RenderedSection[] = [];
  for (const spec of profile.appendices) {
    const part = spec.pick(parts);
    if (part != null) {
      appendixSections.push({ id: spec.id, title: spec.title, body: spec.render(part, ctx) });
    }
  }

  // ── Build the interleaved render list ───────────────────────────────────────
  const tdIdx = numbered.findIndex((s) => s.id === 'eu-sec-td');
  const renderOrder: RenderedSection[] = [];
  numbered.forEach((s, i) => {
    renderOrder.push(s);
    if (i === tdIdx && gspr) {
      renderOrder.push(gspr);
    }
  });
  if (tdIdx === -1 && gspr) {
    renderOrder.push(gspr);
  }
  for (const a of appendixSections) {
    renderOrder.push(a);
  }

  const downloadAction = (
    <Button variant="brand" size="sm" asChild>
      <a
        href={url('pdf')}
        target="_blank"
        rel="noopener"
        title="Download the full NI IVDR dossier as PDF"
      >
        <Download className="size-3.5" /> Download PDF
      </a>
    </Button>
  );

  return (
    <div>
      <NiIvdrDossierHeader response={headerResponse} />

      {/* Sections — full-width, single column. */}
      <div className="mt-6 space-y-8">
        {renderOrder.map((s, i) => (
          <SectionBlock key={s.id} view="CombinedNiIvdrDossierView" section={s} action={i === 0 ? downloadAction : undefined} />
        ))}
      </div>
    </div>
  );
}
